import { Box, Chip, Tooltip, useTheme } from '@mui/material'
import { Replay } from '@mui/icons-material'
import { GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetBackgroundJobs, useTriggerBackgroundJob } from 'src/api/background-jobs'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import { IBackgroundJobDto } from 'src/generated-types/backgroundJob'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { describeCron } from './BackgroundJobsScreen.action'

type JobStatusColor = 'success' | 'error' | 'info' | 'default'

const statusColor = (status?: string): JobStatusColor => {
  if (status === 'Succeeded') return 'success'
  if (status === 'Failed') return 'error'
  if (status === 'Processing' || status === 'Enqueued' || status === 'Scheduled') return 'info'
  return 'default'
}

const BackgroundJobsScreen = () => {
  const { t, i18n } = useTranslation('background-jobs')
  const theme = useTheme()
  const [triggeringId, setTriggeringId] = useState<string | null>(null)

  const { data: jobs, isFetching, refetch } = useGetBackgroundJobs()
  const { mutateAsync: triggerJob } = useTriggerBackgroundJob()

  const handleTrigger = useCallback(
    async (job: IBackgroundJobDto) => {
      setTriggeringId(job.id)
      try {
        await triggerJob({ id: job.id })
        toast.success(t('trigger-success', { jobName: job.jobName }))
        refetch()
      } catch {
        toast.error(t('trigger-failed', { jobName: job.jobName }))
      } finally {
        setTriggeringId(null)
      }
    },
    [t, triggerJob, refetch],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'jobName',
        headerName: t('col-job-name'),
        flex: 2,
        minWidth: 220,
      },
      {
        field: 'cron',
        headerName: t('col-schedule'),
        flex: 1,
        minWidth: 160,
        renderCell: ({ value }) => (
          <Tooltip title={value}>
            <span>{describeCron(value, t, i18n.language)}</span>
          </Tooltip>
        ),
      },
      {
        field: 'lastExecution',
        headerName: t('col-last-run'),
        flex: 1,
        minWidth: 180,
        type: 'string' as GridNativeColTypes,
        valueGetter: ({ value }) => (value ? new Date(value).toLocaleString() : t('never')),
      },
      {
        field: 'nextExecution',
        headerName: t('col-next-run'),
        flex: 1,
        minWidth: 180,
        valueGetter: ({ value }) => (value ? new Date(value).toLocaleString() : '-'),
      },
      {
        field: 'lastJobState',
        headerName: t('col-status'),
        width: 140,
        renderCell: ({ value, row }) => {
          if (!value) return null
          const chip = <Chip label={value} color={statusColor(value)} size="small" />
          return row.error ? <Tooltip title={row.error}>{chip}</Tooltip> : chip
        },
      },
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as GridNativeColTypes,
        width: 160,
        renderCell: ({ row }) => (
          <GridActionButton
            variant="outlined"
            buttonIcon={<Replay />}
            buttonText={t('retrigger')}
            loading={triggeringId === row.id}
            disabled={triggeringId === row.id}
            onClick={() => handleTrigger(row)}
          />
        ),
      },
    ],
    [t, triggeringId, handleTrigger, i18n.language],
  )

  return (
    <CommonPageLayout titleSection={{ title: t('page-title') }}>
      <Box padding="8px" bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius="4px">
        <GenericTable
          id="background-jobs"
          data={jobs ?? []}
          columns={columns}
          getRowId={row => row.id}
          loading={isFetching}
          enableToolbar={false}
          enablePagination={false}
          autoHeight
          getRowHeight={() => 52}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default BackgroundJobsScreen
